import { useMemo, useState } from 'react';
import { BellRing, Send, Users } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { feesApi } from '@/features/institute/fees/api/fees.api';
import { DueWhenFilter } from '@/features/institute/fees/components/DueWhenFilter';
import {
  DUE_WHEN_CHOICES,
  dueWhenFilter,
  type DueWhenChoice,
} from '@/features/institute/fees/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatDate } from '@/lib/format';
import type { ClassFeeDue } from '@/types/fees';
import {
  ClassFeeReminderModal,
  type ClassReminderTarget,
} from '../components/fees/ClassFeeReminderModal';

/**
 * The list's filter, in the address bar, so stepping away and coming back
 * finds the same families listed.
 */
type Filters = {
  due: DueWhenChoice;
};

const DEFAULT_FILTERS: Filters = { due: 'ANY' };

const FILTER_OPTIONS = {
  allowed: { due: DUE_WHEN_CHOICES.map((choice) => choice.key) },
};

/**
 * A class teacher's fee reminders: which families in their own class still owe,
 * which installment and by when — and a reminder to them by SMS.
 *
 * Never an amount or a payment: what a household owes is the office's to read.
 * The server fences everything here to the sections the teacher is class
 * teacher of this year, and refuses anyone who is not one, which this page
 * turns into a plain explanation rather than an error.
 */
export function TeacherFeeRemindersPage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const dueChoice = filters.due;
  const filter = dueWhenFilter(dueChoice);
  const { data, isLoading, error, refetch } = useApiQuery(
    () => feesApi.classDues(dueWhenFilter(dueChoice)),
    [dueChoice],
  );
  /** Ticked families, by student uuid. Cleared when the due-date choice changes. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<ClassReminderTarget | null>(null);

  const students = useMemo(() => data?.students ?? [], [data]);
  const chosen = students.filter((student) => selected.has(student.studentUuid));
  const allTicked = students.length > 0 && chosen.length === students.length;
  const isNotClassTeacher = error?.code === 'NOT_CLASS_TEACHER';

  function toggle(uuid: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  const columns: Column<ClassFeeDue>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          label="Select every family listed"
          hideLabel
          checked={allTicked}
          onChange={() =>
            setSelected(
              allTicked ? new Set() : new Set(students.map((student) => student.studentUuid)),
            )
          }
        />
      ),
      render: (row) => (
        <Checkbox
          label={`Select ${row.fullName}`}
          hideLabel
          checked={selected.has(row.studentUuid)}
          onChange={() => toggle(row.studentUuid)}
        />
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.fullName}</p>
          <p className="text-xs text-ink-3">{row.admissionNumber}</p>
        </div>
      ),
    },
    { key: 'roll', header: 'Roll no.', hideOnMobile: true, render: (row) => row.rollNumber ?? '—' },
    { key: 'class', header: 'Class', hideOnMobile: true, render: (row) => row.sectionName ?? '—' },
    { key: 'installment', header: 'Installment', render: (row) => row.installment ?? 'Fees' },
    {
      key: 'due',
      header: 'Due date',
      render: (row) => (row.dueDate ? formatDate(row.dueDate) : 'Not set'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.dueDate === null ? (
          <Badge tone="neutral">No date</Badge>
        ) : row.isOverdue ? (
          <Badge tone="critical">Overdue</Badge>
        ) : (
          <Badge tone="warning">Due</Badge>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Fee Reminders"
        description="Families in your class with fees still due. Remind them by SMS — the office sees the amounts, you see who and when."
      />

      {isNotClassTeacher ? (
        <div className="card">
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title="For class teachers"
            description="Fee reminders are sent by a class teacher for their own class. You are not class teacher of a section this year — the school office sends reminders for every class."
          />
        </div>
      ) : (
        <section
          className="card flex flex-col gap-4 p-4 sm:p-5"
          aria-label="Families with fees due"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <DueWhenFilter
              value={dueChoice}
              onChange={(choice) => {
                setFilters({ due: choice });
                setSelected(new Set());
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              {chosen.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Send className="h-4 w-4" />}
                  onClick={() => setTarget({ kind: 'SELECTED', students: chosen })}
                >
                  Remind {chosen.length} selected
                </Button>
              )}
              <Button
                size="sm"
                leftIcon={<BellRing className="h-4 w-4" />}
                disabled={students.length === 0}
                onClick={() => setTarget({ kind: 'ALL_DUE', students, ...filter })}
              >
                Remind all {students.length > 0 ? students.length : ''}
              </Button>
            </div>
          </div>

          {error && !isNotClassTeacher ? (
            <QueryError title="Could not load your class's fees" error={error} onRetry={refetch} />
          ) : (
            <Table
              variant="plain"
              columns={columns}
              rows={students}
              rowKey={(row) => row.studentUuid}
              isLoading={isLoading}
              empty={
                <EmptyState
                  icon={<BellRing className="h-5 w-5" />}
                  title={
                    dueChoice === 'ANY'
                      ? 'Nobody in your class owes fees'
                      : 'Nobody matches this choice'
                  }
                  description={
                    dueChoice === 'ANY'
                      ? 'Every family in your class is paid up for this session.'
                      : 'Choose another option to see more families. Families with no due date set appear under Any time.'
                  }
                />
              }
            />
          )}
        </section>
      )}

      {target && (
        <ClassFeeReminderModal
          target={target}
          session={data?.session ?? null}
          onClose={() => setTarget(null)}
          onSent={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}
